
import { Component, Vue } from "vue-property-decorator";
import { TranslateResult } from "vue-i18n";

const stripeImage = require("@/assets/images/stripe-image.jpeg");

interface Order {
  id?: number;
  token?: string;
  paymentUrl?: string;
  paymentStatus?: string;
  status?: string;
  acceptsTermsAndConditions?: boolean;
  trackingNumber: string;
  carrier: string;
  deliveryAddress: {
    name: string;
    address: string;
    zipcode: string;
    city: string;
    country: string;

  };
  billingAddress: {
    name: string;
    address: string;
    zipcode: string;
    city: string;
    country: string;
  };
}

@Component
export default class OrderPage extends Vue {
  private valid = true;
  private step = 1;
  private loading = true;
  private loadingUpdateByToken = false;
  private acceptsTermsAndConditions: boolean = false;
  private useSameBillingAddressThanDeliveryAddress: boolean = false;
  private cguLink: string = "https://info-myloby.s3.eu-west-3.amazonaws.com/Myloby_CGV_CGU.pdf";
  private stripeImage = stripeImage;


  private order: Order = {
    trackingNumber: "",
    carrier: "La Poste",
    deliveryAddress : {
      name: "",
      address: "",
      zipcode: "",
      city: "",
      country: "",
    },
    billingAddress  : {
      name: "",
      address: "",
      zipcode: "",
      city: "",
      country: "",
    },
    acceptsTermsAndConditions: false,
  };

  private deliveryNameRules = [(v: string) => !!v || this.$t("orderPage.deliveryNameRulesRequiredError")];
  private addressRules = [(v: string) => !!v || this.$t("orderPage.addressRequiredError")];
  private zipcodeRules = [(v: string) => !!v || this.$t("orderPage.zipcodeRequiredError")];
  private cityRules = [(v: string) => !!v || this.$t("orderPage.cityRequiredError")];
  private countryRules = [(v: string) => !!v || this.$t("orderPage.countryRequiredError")];

  private billingNameRules = [(v: string) => (this.useSameBillingAddressThanDeliveryAddress  || !!v) || this.$t("orderPage.deliveryNameRulesRequiredError")];
  private billingAddressRules = [(v: string) => (this.useSameBillingAddressThanDeliveryAddress  || !!v) || this.$t("orderPage.addressRequiredError")];
  private billingZipcodeRules = [(v: string) => (this.useSameBillingAddressThanDeliveryAddress  || !!v) || this.$t("orderPage.zipcodeRequiredError")];
  private billingCityRules = [(v: string) => (this.useSameBillingAddressThanDeliveryAddress  || !!v) || this.$t("orderPage.cityRequiredError")];
  private billingCountryRules = [(v: string) => (this.useSameBillingAddressThanDeliveryAddress  || !!v) || this.$t("orderPage.countryRequiredError")];

  private cguRules = [(v: string) => !!v || this.$t("orderPage.cguRequiredError")];
  private token: string;

  public mounted() {
    this.token =
      (this.$route.query.order_token ||
      this.$route.query.token) as string;

    if (this.token) {
      this.$store.dispatch("public/orders/readByToken", { token: this.token })
        .then((response) => {

          const order = response.order;
          const building = response.building;
          this.loading = false;
          this.order.id = order.id;
          this.order.token = order.token;
          this.order.paymentUrl = order.paymentUrl;
          this.order.status = order.status;
          this.order.paymentStatus = order.paymentStatus;
          this.order.trackingNumber = order.trackingNumber;

          // address
          this.order.deliveryAddress.name = building.Address.name || "";
          this.order.deliveryAddress.address = building.Address.address;
          this.order.deliveryAddress.zipcode = building.Address.zipcode;
          this.order.deliveryAddress.city = building.Address.city;
          this.order.deliveryAddress.country = building.Address.country;
          if (
            order.acceptsTermsAndConditions
          ) {
            this.order.deliveryAddress = JSON.parse(JSON.stringify(order.deliveryAddress));
            this.order.billingAddress = JSON.parse(JSON.stringify(order.billingAddress));
            this.order.carrier = order.carrier;
            this.order.acceptsTermsAndConditions = !!order.acceptsTermsAndConditions;

            if (this.order.paymentStatus === "complete" || this.order.paymentStatus === "paid") {
              this.step = 3;
            } else {
              this.step = 2;
            }
          }
        });
    } else {
      this.$store.commit("alerts/displayError", {
        message: this.$i18n?.t("alerts.error.notFound"),
      });
    }
  }

  public submitForm() {
    const formValidation = (this.$refs.form as any).validate();
    if (!formValidation) {
      (this.$refs.form as any).$children.forEach((field: any) => {
        if (field.hasError) {
          // tslint:disable-next-line:no-console
          console.log(`Field: ${field.label || field.$attrs.label}, Error: ${field.errorMessages}`);
        }
      });
      return;
    } else {
      if (this.useSameBillingAddressThanDeliveryAddress) {
        this.order.billingAddress = JSON.parse(JSON.stringify(this.order.deliveryAddress));
      }
      this.loadingUpdateByToken = true;
      this.$store.dispatch("public/orders/updateByToken", { order: this.order })
        .then(() => {
          this.$store.dispatch("public/orders/readByToken", { token: this.token })
            .then((response) => {
              if (response.order?.paymentUrl) {
                this.order.paymentUrl = response.order.paymentUrl;
              }
              this.step = 2;
            });
        })
        .catch((error) => {
          this.$store.commit("alerts/displayError", {
            msg: this.$i18n?.t(error.response?.data?.name),
          });
        })
        .finally(() => {
          this.loadingUpdateByToken = false;
        });
    }

  }

  public getTrackingNumberLink(trackingNumber: string): string {
    return `https://www.laposte.fr/outils/suivre-vos-envois?code=${trackingNumber}`;
  }

  public getStatusLabel(status: string): string {
    const statusLabels: { [key: string]: string | TranslateResult } = {
      complete: this.$t("orderPage.paymentStatusComplete"),
      paid: this.$t("orderPage.paymentStatusComplete"),
      PAYMENT_MISSING: this.$t("orderPage.paymentStatusMissing"),
      UNDER_CONSTRUCTION: this.$t("orderPage.paymentStatusUnderConstruction"),
      IN_DELIVERY: this.$t("orderPage.statusInDelivery"),
      ERROR: this.$t("orderPage.error"),
    };

    const translatedStatus: TranslateResult = statusLabels[status] || "";
    return translatedStatus.toString();
  }
}
